import { useEffect, useRef, useState } from"react";
import { Sparkles, Send, X, RotateCcw, ArrowUpRight, Cpu, Zap } from"lucide-react";
import { answerLocal } from"@/lib/assistantLocal";
import renderMarkdown from"@/lib/markdownRender";

const STORE_KEY ="keyto-assistant-history-v2";
const STORE_SESSION ="keyto-assistant-session";
const STORE_MODE ="keyto-assistant-mode"; //"local" |"ai"
const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Mini markdown renderer (gras, code inline, tableaux, listes, titres) — léger, sans dépendance.
// Le rendu est délégué à @/lib/markdownRender qui retourne des éléments React (pas d'HTML brut),
// ce qui supprime toute surface XSS via dangerouslySetInnerHTML.

const STARTERS = ["Quelle différence entre 411 et 416 ?","Comment comptabiliser un achat de marchandises ?","Compte 707","Qu'est-ce qu'un amortissement ?",
];

// Génère un ID de session stable par navigateur
function getSessionId() {
 let id = localStorage.getItem(STORE_SESSION);
 if (!id) {
 id = `keyto-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
 localStorage.setItem(STORE_SESSION, id);
 }
 return id;
}

export default function Assistant({ onSelectAccount, onChangeView }) {
 const [open, setOpen] = useState(false);
 const [mode, setMode] = useState(() => localStorage.getItem(STORE_MODE) ||"local");
 const [messages, setMessages] = useState(() => {
 try { return JSON.parse(localStorage.getItem(STORE_KEY) ||"[]"); }
 catch (e) { console.warn("Assistant: corrupt history in localStorage", e); return []; }
 });
 const [input, setInput] = useState("");
 const [streaming, setStreaming] = useState(false);
 const scrollRef = useRef(null);
 const abortRef = useRef(null);

 useEffect(() => { localStorage.setItem(STORE_KEY, JSON.stringify(messages)); }, [messages]);
 useEffect(() => { localStorage.setItem(STORE_MODE, mode); }, [mode]);
 useEffect(() => {
 scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior:"smooth" });
 }, [messages, open, streaming]);

 // Envoi en mode local : réponse instantanée et gratuite
 const sendLocal = (text) => {
 const userMsg = { role:"user", content: text };
 const { md, sources } = answerLocal(text);
 const assistMsg = { role:"assistant", content: md, sources, engine:"local" };
 setMessages((m) => [...m, userMsg, assistMsg]);
 };

 // Envoi en mode IA : streaming SSE → Claude Haiku 4.5 ; fallback local en cas d'échec
 const sendAI = async (text) => {
 const userMsg = { role:"user", content: text };
 setMessages((m) => [...m, userMsg, { role:"assistant", content:"", engine:"ai", streaming: true }]);
 setStreaming(true);

 const historyForApi = messages
 .filter((m) => !m.streaming)
 .map((m) => ({ role: m.role, content: m.content }));

 const ctrl = new AbortController();
 abortRef.current = ctrl;

 try {
 const res = await fetch(`${API_BASE}/api/assistant/chat`, {
 method:"POST",
 headers: {"Content-Type":"application/json" },
 body: JSON.stringify({
 message: text,
 session_id: getSessionId(),
 history: historyForApi,
 }),
 signal: ctrl.signal,
 });

 if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

 const reader = res.body.getReader();
 const decoder = new TextDecoder();
 let buf ="";
 let acc ="";

 while (true) {
 const { done, value } = await reader.read();
 if (done) break;
 buf += decoder.decode(value, { stream: true });
 const events = buf.split("\n\n");
 buf = events.pop();
 for (const ev of events) {
 const line = ev.trim();
 if (!line.startsWith("data:")) continue;
 try {
 const data = JSON.parse(line.slice(5).trim());
 if (data.delta) {
 acc += data.delta;
 setMessages((m) => {
 const next = [...m];
 next[next.length - 1] = { ...next[next.length - 1], content: acc };
 return next;
 });
 } else if (data.error) {
 throw new Error(data.error);
 } else if (data.done) {
 // fin de stream
 }
 } catch (e) { console.warn("SSE chunk parse failed", e); }
 }
 }
 setMessages((m) => {
 const next = [...m];
 next[next.length - 1] = { ...next[next.length - 1], streaming: false };
 return next;
 });
 } catch (e) {
 // Fallback : on remplace le placeholder par une réponse locale
 const { md, sources } = answerLocal(text);
 setMessages((m) => {
 const next = [...m];
 next[next.length - 1] = {
 role:"assistant",
 content: `_⚠️ IA indisponible, réponse locale :_\n\n${md}`,
 sources,
 engine:"local",
 streaming: false,
 };
 return next;
 });
 } finally {
 setStreaming(false);
 abortRef.current = null;
 }
 };

 const send = (text) => {
 if (!text.trim() || streaming) return;
 setInput("");
 if (mode ==="ai") sendAI(text);
 else sendLocal(text);
 };

 const reset = () => {
 abortRef.current?.abort();
 localStorage.removeItem(STORE_KEY);
 setMessages([]);
 setStreaming(false);
 };

 return (
 <>
 {!open && (
 <button
 data-testid="assistant-open"
 onClick={() => setOpen(true)}
 className="fixed bottom-5 right-5 z-50 group flex items-center gap-2 pl-4 pr-5 py-3 rounded-full text-white shadow-lg bg-gradient-to-br from-[#2D6BE0] to-[#15294F] hover:scale-105 transition-transform"
 style={{ boxShadow:"0 10px 30px -10px rgba(45,107,224,0.5)" }}
 >
 <Sparkles size={18} className="text-[#5AA0FF]" />
 <span className="text-sm font-bold">Agent IA</span>
 </button>
 )}

 {open && (
 <div
 data-testid="assistant-panel"
 className="fixed bottom-5 right-5 z-50 w-[calc(100vw-2.5rem)] sm:w-[420px] md:w-[480px] h-[min(680px,calc(100vh-3rem))] bg-white rounded-2xl shadow-2xl border border-keyto flex flex-col overflow-hidden no-print"
 style={{ boxShadow:"0 20px 50px -10px rgba(21,41,79,0.35)" }}
 >
 <header className="px-4 py-3 bg-gradient-to-br from-[#15294F] to-[#1F3B6B] text-white flex items-center gap-3">
 <div className="w-9 h-9 rounded-full bg-white/10 border border-white/20 flex items-center justify-center">
 <Sparkles size={16} className="text-[#5AA0FF]" />
 </div>
 <div className="flex-1 min-w-0">
 <p className="font-heading font-bold text-sm leading-tight">Agent IA</p>
 <p className="text-[11px] text-white/60 leading-tight">
 {mode ==="ai" ?"Claude Haiku 4.5 · contexte PCG 2025" :"Moteur local · 207 fiches + 24 schémas"}
 </p> </div>
 <button
 data-testid="assistant-reset"
 onClick={reset}
 title="Nouvelle conversation"
 className="p-1.5 rounded hover:bg-white/10 text-white/70"
 >
 <RotateCcw size={14} />
 </button>
 <button
 data-testid="assistant-close"
 onClick={() => setOpen(false)}
 className="p-1.5 rounded hover:bg-white/10 text-white/70"
 >
 <X size={16} />
 </button>
 </header>

 {/* Toggle mode local/IA */}
 <div data-testid="assistant-mode-toggle" className="flex border-b border-keyto bg-slate-50 text-xs">
 <button
 data-testid="assistant-mode-local"
 onClick={() => setMode("local")}
 className={`flex-1 px-3 py-2 flex items-center justify-center gap-1.5 font-semibold transition ${
 mode ==="local"
 ?"bg-white text-[#15294F] border-b-2 border-[#2D6BE0]"
 :"text-slate-500 hover:bg-white/50"
 }`}
 >
 <Zap size={12} /> Local
 </button>
 <button
 data-testid="assistant-mode-ai"
 onClick={() => setMode("ai")}
 className={`flex-1 px-3 py-2 flex items-center justify-center gap-1.5 font-semibold transition ${
 mode ==="ai"
 ?"bg-white text-[#15294F] border-b-2 border-[#2D6BE0]"
 :"text-slate-500 hover:bg-white/50"
 }`}
 >
 <Cpu size={12} /> IA · Claude
 </button>
 </div>

 <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50">
 {messages.length === 0 && (
 <div className="space-y-3" data-testid="assistant-welcome">
 <div className="text-sm text-slate-600 leading-relaxed">
 Bonjour 👋 Je suis l&apos;<strong className="text-[#15294F]">Agent IA</strong> du Cabinet KEYTO.
 {mode ==="local"
 ? <> En mode local, je puise dans les <strong>207 fiches PCG</strong>, <strong>24 schémas</strong> et le <strong>glossaire</strong>.</>
 : <> En mode IA, je sollicite <strong>Claude Haiku 4.5</strong> pour répondre à des questions plus ouvertes sur la comptabilité.</>
 }
 </div>
 <div className="space-y-1.5 pt-2">
 <p className="text-[10px] font-bold uppercase text-slate-400 tracking-wider">Exemples</p>
 {STARTERS.map((s, i) => (
 <button
 key={i}
 data-testid={`assistant-starter-${i}`}
 onClick={() => send(s)}
 className="w-full text-left text-xs px-3 py-2 rounded-lg border border-keyto bg-white hover:bg-slate-50 hover:border-[#2D6BE0]/40 text-slate-700"
 >
 {s}
 </button>
 ))}
 </div>
 </div>
 )}

 {messages.map((m, i) => (
 <div
 key={i}
 data-testid={`assistant-msg-${m.role}-${i}`}
 className={`flex ${m.role ==="user" ?"justify-end" :"justify-start"}`}
 >
 <div
 className={`max-w-[92%] rounded-2xl px-3.5 py-2.5 ${
 m.role ==="user"
 ?"bg-[#2D6BE0] text-white rounded-br-md"
 :"bg-white border border-keyto text-slate-800 rounded-bl-md"
 }`}
 >
 {m.role ==="user" ? (
 <p className="text-sm leading-relaxed whitespace-pre-wrap">{m.content}</p>
 ) : (
 <>
 {m.engine && (
 <div className="flex items-center gap-1.5 mb-1.5 text-[9px] font-bold uppercase tracking-wider text-slate-400">
 {m.engine ==="ai" ? <><Cpu size={9} /> Claude Haiku 4.5</> : <><Zap size={9} /> Moteur local</>}
 {m.streaming && <span className="ml-1 animate-pulse text-[#2D6BE0]">●</span>}
 </div>
 )}
 {m.content ? (
 <div className="prose prose-sm max-w-none">
 {renderMarkdown(m.content)}
 </div>
 ) : (
 <p className="text-xs text-slate-400 italic">L&apos;agent réfléchit…</p>
 )}
 {m.sources && m.sources.length > 0 && (
 <div className="mt-3 pt-2 border-t border-keyto flex flex-wrap gap-1.5">
 {m.sources.map((s, j) => {
 const onClick = () => {
 if (s.type ==="compte" && onSelectAccount) onSelectAccount(s.num);
 else if (s.type ==="ecriture" && onChangeView) onChangeView("ecritures");
 else if (s.type ==="glossaire" && onChangeView) onChangeView("glossaire");
 };
 return (
 <button
 key={j}
 onClick={onClick}
 data-testid={`assistant-source-${i}-${j}`}
 className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-[#2D6BE0] hover:bg-blue-100 transition"
 >
 {s.type ==="compte" ? `Compte ${s.num}` : s.label}
 <ArrowUpRight size={10} />
 </button>
 );
 })}
 </div>
 )}
 </>
 )}
 </div>
 </div>
 ))}
 </div>

 <form
 onSubmit={(e) => { e.preventDefault(); send(input); }}
 className="px-3 py-3 border-t border-keyto bg-white flex items-end gap-2"
 >
 <textarea
 data-testid="assistant-input"
 value={input}
 onChange={(e) => setInput(e.target.value)}
 onKeyDown={(e) => {
 if (e.key ==="Enter" && !e.shiftKey) {
 e.preventDefault();
 send(input);
 }
 }}
 placeholder={streaming ?"L'agent répond…" :"Pose ta question (compte, écriture, terme…)"}
 rows={1}
 disabled={streaming}
 className="flex-1 resize-none rounded-lg border border-keyto px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2D6BE0]/30 focus:border-[#2D6BE0] max-h-24 disabled:bg-slate-50"
 />
 <button
 data-testid="assistant-send"
 type="submit"
 disabled={!input.trim() || streaming}
 className="shrink-0 w-9 h-9 rounded-lg bg-[#2D6BE0] text-white flex items-center justify-center hover:bg-[#15294F] disabled:opacity-40 disabled:cursor-not-allowed transition"
 >
 <Send size={14} />
 </button>
 </form>
 </div>
 )}
 </>
 );
}
